import { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { apiService, CallChannel, CallVolumeResponse } from '../../../services/api';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { CallVolumeTab } from './CallVolumeTab';
import { LABELS } from './labels';
import { buildMonthParam } from './format';

/** Selectable years: current year ±2 (matches BillingPage). */
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

const CHANNELS: CallChannel[] = ['voice', 'chat', 'combined'];

/**
 * Admin "Anrufvolumen" page (CA-586): per-month Voice / Chat / combined session
 * volume with per-day counts and peak concurrency. Visible to all portal admins.
 */
export function CallVolumePage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState<CallVolumeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<CallChannel>('voice');

  useEffect(() => {
    const monthParam = buildMonthParam(year, month);
    let cancelled = false;

    const fetchCallVolume = async () => {
      try {
        setLoading(true);
        const response = await apiService.getCallVolume(monthParam);
        if (!cancelled) {
          setData(response);
          setError(null);
        }
      } catch (err) {
        console.error('Failed to fetch call volume:', err);
        if (!cancelled) {
          setError(extractErrorMessage(err, LABELS.states.error, 'load-call-volume'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchCallVolume();
    return () => {
      cancelled = true;
    };
  }, [year, month]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{LABELS.page.title}</h1>
          <p className="text-gray-600">{LABELS.page.subtitle}</p>
        </div>

        {/* Month + year selector */}
        <div className="flex items-center space-x-3">
          <Calendar className="w-4 h-4 text-gray-500" />
          <Select value={String(month)} onValueChange={(value) => setMonth(Number(value))}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LABELS.months.map((name, index) => (
                <SelectItem key={index + 1} value={String(index + 1)}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(value) => setYear(Number(value))}>
            <SelectTrigger className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {YEAR_OPTIONS.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : error ? (
        <div className="text-center py-24">
          <p className="text-red-600">{error}</p>
        </div>
      ) : data ? (
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as CallChannel)} className="space-y-4">
          <TabsList>
            {CHANNELS.map((channel) => (
              <TabsTrigger key={channel} value={channel}>
                {LABELS.tabs[channel]}
              </TabsTrigger>
            ))}
          </TabsList>
          {CHANNELS.map((channel) => (
            <TabsContent key={channel} value={channel}>
              <CallVolumeTab channel={channel} data={data[channel]} />
            </TabsContent>
          ))}
        </Tabs>
      ) : null}
    </div>
  );
}
